import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FleetTabs, PageHeader, Pill, StatusPill, CAMPAIGN_COLORS, JOB_COLORS, ACTIONS, fmtDate, errText, useLiveRefresh } from './common';
import { describeSelector, JobBar } from './FleetCampaigns';

export function JobOutputModal({ jobId, onClose }) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get(`/cve-fleet/jobs/${jobId}`).then(r => setJob(r.data)).catch(e => setError(errText(e, 'Failed to load the job')));
  }, [jobId]);
  const pre = { whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 320, overflow: 'auto', background: 'var(--bg-secondary, rgba(127,127,127,.08))', padding: 10, borderRadius: 6 };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, width: '90%' }}>
        <div className="modal-header"><h3>Job #{jobId}{job ? ` · ${job.action} on ${job.host_cn}` : ''}</h3></div>
        <div className="modal-body">
          {error && <div className="error-msg">{error}</div>}
          {!job ? <div className="spinner" /> : (
            <>
              <div style={{ fontSize: 13, marginBottom: 10 }}>
                <Pill label={job.state} color={JOB_COLORS[job.state]} /> exit {job.exit_code ?? '—'} · queued {fmtDate(job.queued_at)} · finished {fmtDate(job.finished_at)}
              </div>
              <div className="form-label">stdout</div>
              <pre style={pre}>{job.stdout || '(empty)'}</pre>
              <div className="form-label" style={{ marginTop: 10 }}>stderr</div>
              <pre style={pre}>{job.stderr || '(empty)'}</pre>
            </>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

export default function FleetCampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [state, setState] = useState('');
  const [error, setError] = useState('');
  const [jobId, setJobId] = useState(null);

  const load = useCallback(async () => {
    try {
      const q = state ? `?state=${state}` : '';
      const [a, b] = await Promise.all([axios.get(`/cve-fleet/campaigns/${id}`), axios.get(`/cve-fleet/campaigns/${id}/jobs${q}`)]);
      setC(a.data); setJobs(b.data.jobs); setError('');
    } catch (e) { setError(errText(e, 'Failed to load the campaign')); }
  }, [id, state]);
  useEffect(() => { load(); }, [load]);
  const live = c && (c.state === 'running' || (c.jobs.dispatched || 0) + (c.jobs.queued || 0) > 0);
  useLiveRefresh(load, 10000, live);

  async function op(name, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      const r = await axios.post(`/cve-fleet/campaigns/${id}/${name}`, {});
      if (name === 'rollback') navigate(`/cve-fleet/campaigns/${r.data.id}`); else load();
    } catch (e) { alert(errText(e, `Failed to ${name}`)); }
  }

  const failures = c ? (c.jobs.failed || 0) + (c.jobs.timed_out || 0) : 0;
  return (
    <div>
      <PageHeader />
      <div className="page-body">
        <FleetTabs />
        <div style={{ marginBottom: 12 }}><Link to="/cve-fleet/campaigns">← All campaigns</Link></div>
        {error && <div className="error-msg">{error}</div>}
        {!c ? <div className="spinner" /> : (
          <>
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0 }}>#{c.id} · {c.action}</h3>
                <Pill label={c.state} color={CAMPAIGN_COLORS[c.state]} />
                {c.reason && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{c.reason}</span>}
                <div style={{ flex: 1 }} />
                {c.state === 'running' && <button className="btn btn-secondary" onClick={() => op('pause')}>Pause</button>}
                {c.state === 'paused' && <button className="btn btn-primary" onClick={() => op('resume', failures ? `Resume? The ${failures} failure(s) so far are accepted; the failure budget applies to new ones.` : null)}>Resume</button>}
                {(c.state === 'running' || c.state === 'paused') && <button className="btn btn-secondary" onClick={() => op('abort', 'Abort? Pending and queued jobs are cancelled; jobs already on a server finish.')}>Abort</button>}
                {c.rollback_action && <button className="btn btn-secondary" style={{ color: 'var(--danger)' }}
                  onClick={() => op('rollback', `Roll back? This aborts the campaign if it is still going, then runs "${c.rollback_action}" on every server it touched.${c.action === 'patch' ? ' After a kernel rollback, run a reboot campaign.' : ''}`)}>Roll back</button>}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', margin: '8px 0' }}>{ACTIONS[c.action]}</div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
                <span>Servers: <strong>{describeSelector(c.selector)}</strong> ({c.total})</span>
                <span>Canary {c.canary} · batch {c.batch_size} · max failures {c.max_failures} · timeout {c.job_timeout}s</span>
                <span>Waves {c.waves}{c.current_wave != null ? ` · running wave ${c.current_wave}` : ''}</span>
                <span>By {c.created_by} · {fmtDate(c.created_at)}</span>
                {c.parent_id && <span>Rolls back <Link to={`/cve-fleet/campaigns/${c.parent_id}`}>#{c.parent_id}</Link></span>}
                {c.note && <span>Note: {c.note}</span>}
              </div>
              <div style={{ marginTop: 12 }}><JobBar jobs={c.jobs} total={c.total} /></div>
            </div>
            <div className="toolbar">
              <select className="form-input" style={{ width: 170 }} value={state} onChange={e => setState(e.target.value)}>
                <option value="">All jobs</option>
                {Object.keys(JOB_COLORS).map(s => <option key={s} value={s}>{s.replace('_', ' ')}{c.jobs[s] ? ` (${c.jobs[s]})` : ''}</option>)}
              </select>
              <div className="spacer" />
              {live && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>● live — refreshing every 10s</span>}
            </div>
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>
                <th style={{ padding: 8 }}>Wave</th><th style={{ padding: 8 }}>Server</th><th style={{ padding: 8 }}>State</th>
                <th style={{ padding: 8 }}>Exit</th><th style={{ padding: 8 }}>Server status now</th><th style={{ padding: 8 }}>Finished</th></tr></thead>
              <tbody>{jobs.map(j => (
                <tr key={j.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setJobId(j.id)}>
                  <td style={{ padding: 8 }}>{j.wave === 0 && c.canary > 0 ? 'canary' : j.wave}</td>
                  <td style={{ padding: 8 }}><Link to={`/cve-fleet/hosts/${encodeURIComponent(j.host_cn)}`} onClick={e => e.stopPropagation()}>{j.host_cn}</Link></td>
                  <td style={{ padding: 8 }}><Pill label={j.state.replace('_', ' ')} color={JOB_COLORS[j.state]} /></td>
                  <td style={{ padding: 8 }}>{j.exit_code ?? '—'}</td>
                  <td style={{ padding: 8 }}><StatusPill status={j.host_status} />{j.host_mitigated ? <> <Pill label="mitigated" color="var(--success)" /></> : null}</td>
                  <td style={{ padding: 8 }}>{fmtDate(j.finished_at)}</td>
                </tr>))}</tbody>
            </table>
          </>
        )}
      </div>
      {jobId && <JobOutputModal jobId={jobId} onClose={() => setJobId(null)} />}
    </div>
  );
}
